import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'

export interface ConversationListItem {
  id: string
  title: string | null
  created_at: string
  last_message_at: string | null
  message_count: number
}

export interface ConversationMessage {
  id: string | number
  role: 'user' | 'assistant' | 'system'
  content: string
  created_at: string
  /** Phase 3 (2026-10): response-linter flags persisted with the answer. */
  lint_flags?: { code: string; detail: string }[]
  model_used?: string
  tokens_used?: number
  /** Phase 4 (2026-10): folded tool exchanges (see get_full_messages). */
  tool_calls?: { id?: string; name: string; input?: Record<string, unknown> }[]
  tool_results?: { tool_call_id?: string; result?: unknown; is_error?: boolean }[]
}

export type FeedbackVerdict = 'up' | 'down' | 'wrong'
export type FeedbackReason = 'wrong_data' | 'unverified' | 'unhelpful' | 'slow' | 'other'

/** Phase 4 (2026-10): 👍 / 👎 / "wrong" on an assistant answer. */
export function postAnswerFeedback(body: {
  conversation_id: string
  message_id?: number
  verdict: FeedbackVerdict
  reason?: FeedbackReason
  note?: string
  answer_excerpt?: string
  context?: Record<string, unknown>
}) {
  return jsonFetch<{ ok: boolean; id: number; eval_candidate: boolean }>('/api/carla/feedback', {
    method: 'POST',
    body,
  })
}

const LINT_LABELS: Record<string, string> = {
  unearned_verification: 'claims verification without a read this turn',
  forbidden_host: 'contains a link on a non-SPI host',
  unseen_studio_field: 'names a Studio field no tool returned',
  studio_recommendation: 'recommends Odoo Studio (SPI policy: module development)',
  known_fake_person: 'names a person who does not exist at SPI',
}

/** Mirror of the middleware's response_lint.banner() for reloaded history. */
export function lintBannerFromFlags(flags?: { code: string; detail: string }[]): string | undefined {
  if (!flags || flags.length === 0) return undefined
  const codes = Array.from(new Set(flags.map((f) => f.code))).sort()
  return '⚠ Not verified: ' + codes.map((c) => LINT_LABELS[c] ?? c).join('; ')
}

export interface ConversationMessagesResponse {
  messages: ConversationMessage[]
  conversation_id: string
}

interface ConversationsResponse {
  conversations: ConversationListItem[]
}

/** List all conversations for the signed-in user. Middleware returns
 *  them in created_at DESC so the most recent sits at the top. */
export function useConversations() {
  return useQuery({
    queryKey: ['carla', 'conversations'],
    queryFn: async () => {
      const res = await jsonFetch<ConversationsResponse>('/api/chat/conversations')
      return res.conversations ?? []
    },
    staleTime: 30_000,
  })
}

/** Full message history for one conversation. Fetched when the user
 *  clicks an item in the list rail. */
export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['carla', 'conversations', conversationId, 'messages'],
    queryFn: async () => {
      if (!conversationId) return { messages: [], conversation_id: '' }
      return jsonFetch<ConversationMessagesResponse>(
        `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      )
    },
    enabled: Boolean(conversationId),
    staleTime: 10_000,
  })
}

/**
 * Sprint 29 — mint a single-use confirm token for a (tool, params) pair.
 *
 * Companion to the server-side T3 gate (Sprint 25). The flow:
 *   1. CARLA dispatches a T3 tool → agent denies via _validate_confirm_token,
 *      returning `confirm_token_required: true` in the tool_result.
 *   2. The chat hook pushes a DestructiveConfirmMessage card.
 *   3. User clicks Confirm → this function runs → returns a 60s
 *      single-use token bound to (issued_by, tool_name, params_hash).
 *   4. Caller invokes `postExecuteConfirmedStream()` (below) with the
 *      token — the deterministic /api/agent/execute-confirmed endpoint
 *      dispatches the exact (tool, params) pair directly, no Claude
 *      re-emit involved.
 *
 * Backend: app/api/routes/agent_confirm.py + app/services/confirm_tokens.py.
 */
export interface IssuedConfirmToken {
  token: string
  expires_at: string
  tool_name: string
}

export async function mintConfirmToken(
  toolName: string,
  params: Record<string, unknown>,
): Promise<IssuedConfirmToken> {
  return jsonFetch<IssuedConfirmToken>('/api/agent/confirm', {
    method: 'POST',
    body: { tool_name: toolName, params },
  })
}


/**
 * Deterministic confirm-execute (2026-05-27).
 *
 * After the user clicks Confirm on a DestructiveConfirmCard, POST the
 * (conversation_id, tool_name, params, token) to this endpoint. The
 * backend validates the token, dispatches the tool directly (no Claude
 * re-emit), persists the synthetic tool_use + tool_result, and streams
 * one Claude summary turn back as SSE — same event shape as
 * /api/chat/stream so the consuming hook reuses its SSE reader.
 *
 * Returns the raw Response so the caller can read the streaming body.
 * On non-OK, the body is `{ detail: { confirm_token_result?: ... } }` —
 * surface via humanizeChatError.
 *
 * Why this exists: the Sprint 29 follow-up-message path trusted Claude
 * to re-emit the exact same tool call so the server's params_hash
 * binding would line up. In practice Claude could pick a different tool
 * (stale staged action in history) or re-emit with subtly different
 * params and the action would silently never run. This endpoint
 * sidesteps that fragility — same (tool, params) we hashed at issuance
 * are the same (tool, params) we dispatch.
 */
export async function postExecuteConfirmedStream(
  conversationId: string,
  toolName: string,
  params: Record<string, unknown>,
  confirmToken: string,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch('/api/agent/execute-confirmed', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      conversation_id: conversationId,
      tool_name: toolName,
      params,
      confirm_token: confirmToken,
    }),
    signal,
  })
}


export function useDeleteConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (conversationId: string) =>
      jsonFetch<void>(`/api/chat/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'DELETE',
      }),
    // Optimistic remove: the row disappears from the sidebar immediately.
    // If the DELETE fails, restore it on rollback.
    onMutate: async (conversationId: string) => {
      const key = ['carla', 'conversations'] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ConversationListItem[]>(key)
      if (previous) {
        qc.setQueryData<ConversationListItem[]>(
          key,
          previous.filter((c) => c.id !== conversationId),
        )
      }
      return { previous }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<ConversationListItem[]>(['carla', 'conversations'], ctx.previous)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['carla', 'conversations'] })
    },
  })
}
